import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap, finalize } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { TraceService } from '../services/trace.service';

/**
 * HTTP interceptor that logs every request AND propagates W3C Trace Context
 * headers so that frontend → backend calls are linked in Grafana Tempo.
 *
 * Headers added:
 *   traceparent  – W3C distributed tracing (00-{traceId}-{spanId}-01)
 *   tracestate   – vendor-specific trace metadata
 *   X-Correlation-Id – legacy correlation for Loki log linking
 *
 * Skipped: requests to /loki, /log, /telemetry, or the OTel collector.
 */
export const LoggingInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(LoggerService);
  const trace = inject(TraceService);

  // Skip logging for calls to observability infrastructure
  const url = req.url.toLowerCase();
  if (
    url.includes('/loki') ||
    url.includes('/v1/traces') ||
    url.includes('/v1/logs') ||
    url.includes('/telemetry')
  ) {
    return next(req);
  }

  const startTime = performance.now();

  // ---- Span creation ------------------------------------------------------
  // Create a child span for this HTTP call so it appears in the Tempo trace
  const httpMethod = req.method;
  const spanName = `${httpMethod} ${req.url}`;
  const span = trace.startSpan(spanName, 'client');
  trace.setAttribute('http.method', httpMethod);
  trace.setAttribute('http.url', req.urlWithParams);
  trace.setAttribute('component', 'HTTP');

  // ---- W3C Trace Context propagation --------------------------------------
  // These headers allow the backend to create child spans under the same trace
  let headers: Record<string, string> = {};

  const traceParent = trace.getTraceParent();
  if (traceParent) {
    headers['traceparent'] = traceParent;
  }

  const traceState = trace.getTraceState();
  if (traceState) {
    headers['tracestate'] = traceState;
  }

  // Legacy correlation ID (still useful for pure-Loki searches)
  let correlationId = req.headers.get('X-Correlation-Id') ?? undefined;
  if (!correlationId) {
    correlationId = logger.generateCorrelationId();
  }
  headers['X-Correlation-Id'] = correlationId;

  req = req.clone({ setHeaders: headers });

  // ---- Execute ------------------------------------------------------------
  let statusCode: number | undefined;
  let responseSize: number | undefined;

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          statusCode = event.status;
          trace.setAttribute('http.status_code', statusCode ?? 0);

          const body = event.body;
          if (typeof body === 'string') {
            responseSize = body.length;
          } else if (body) {
            try {
              responseSize = JSON.stringify(body).length;
            } catch {
              // can't measure – ignore
            }
          }
        }
      },
      error: (error) => {
        statusCode = error?.status ?? 0;

        trace.setAttribute('http.status_code', statusCode ?? 0);
        trace.setError(error);
      },
    }),
    finalize(() => {
      const durationMs = Math.round((performance.now() - startTime) * 100) / 100;
      trace.setAttribute('http.duration_ms', durationMs);

      if (responseSize !== undefined) {
        trace.setAttribute('http.response_size_bytes', responseSize);
      }

      // Finish the span (emits to any registered exporter)
      trace.finishSpan(span);

      // ---- Log entry (enriched with trace context) ------------------------
      const logData: Record<string, unknown> = {
        method: httpMethod,
        url: req.urlWithParams,
        status: statusCode ?? 'unknown',
        durationMs,
        correlationId,
      };

      if (responseSize !== undefined) {
        logData['responseSizeBytes'] = responseSize;
      }
      if (req.body && typeof req.body !== 'string') {
        logData['requestSizeBytes'] = JSON.stringify(req.body).length;
      }

      if (statusCode && statusCode >= 400) {
        // 401 on authentication endpoints is expected (bad credentials) — log at debug
        const isAuthEndpoint = req.url.includes('/authentication/');
        if (statusCode === 401 && isAuthEndpoint) {
          logger.debug('HTTP', `${httpMethod} ${req.urlWithParams} → ${statusCode} (${durationMs}ms)`, logData);
        } else if (statusCode >= 500) {
          logger.error('HTTP', `${httpMethod} ${req.urlWithParams} → ${statusCode} (${durationMs}ms)`, undefined, logData);
        } else {
          logger.warn('HTTP', `${httpMethod} ${req.urlWithParams} → ${statusCode} (${durationMs}ms)`, logData);
        }
      } else {
        logger.debug('HTTP', `${httpMethod} ${req.urlWithParams} → ${statusCode ?? '?'} (${durationMs}ms)`, logData);
      }
    }),
  );
};
